<script setup lang="ts">
import { ref, useId } from 'vue'
import { Eye } from 'lucide-vue-next'
import { Button } from '@/ui/button'
import RecoveryExperienceModal from './RecoveryExperienceModal.vue'
import { RECOVERY_METHODS, type RecoveryMethodKey } from './methods'
import { useRecoveryConfig, type MethodSelection } from './useRecoveryConfig'

const { t } = useI18n()
const { onlineUrl } = useRecoveryConfig()

const uid = useId()

const selection = defineModel<MethodSelection>({ required: true })

const isActive = (key: RecoveryMethodKey) => selection.value.methods.includes(key)

const toggle = (key: RecoveryMethodKey, on: boolean) => {
  if (on) {
    if (!isActive(key)) selection.value.methods = [...selection.value.methods, key]
  } else {
    selection.value.methods = selection.value.methods.filter((k) => k !== key)
  }
}

const previewKey = ref<RecoveryMethodKey | null>(null)
const previewOpen = ref(false)

const openExperience = (key: RecoveryMethodKey) => {
  previewKey.value = key
  previewOpen.value = true
}

const methodLabel = (key: RecoveryMethodKey) => t(`pages.loyaltyPage.tabs.recovery.methods.${key}.label`)
const methodDescription = (key: RecoveryMethodKey) => t(`pages.loyaltyPage.tabs.recovery.methods.${key}.description`)
</script>

<template>
  <div class="space-y-3">
    <div
      v-for="method in RECOVERY_METHODS"
      :key="method.key"
      class="rounded-xl border p-4 transition-colors"
      :class="isActive(method.key) ? 'border-primary' : 'border-border'"
    >
      <div class="flex items-center gap-4">
        <span
          class="flex size-10 shrink-0 items-center justify-center rounded-lg transition-colors"
          :class="isActive(method.key) ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'"
        >
          <component :is="method.icon" class="size-5" />
        </span>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-foreground">{{ methodLabel(method.key) }}</p>
          <p class="mt-0.5 text-sm text-muted-foreground">{{ methodDescription(method.key) }}</p>
        </div>
        <AppTooltip :text="t('pages.loyaltyPage.tabs.recovery.methodPicker.seeExperience')">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            class="shrink-0"
            :aria-label="t('pages.loyaltyPage.tabs.recovery.methodPicker.seeExperience')"
            @click="openExperience(method.key)"
          >
            <Eye />
          </Button>
        </AppTooltip>
        <BaseSwitch
          :id="`${uid}-method-${method.key}`"
          :model-value="isActive(method.key)"
          @update:model-value="toggle(method.key, $event)"
        />
      </div>

      <div v-if="method.key === 'online' && isActive('online')" class="mt-4 border-t border-border pt-4">
        <AppFormInput
          :id="`${uid}-online-url`"
          v-model="onlineUrl"
          type="text"
          :required="true"
          :label="t('pages.loyaltyPage.tabs.recovery.onlineUrlLabel')"
        />
        <p class="mt-1.5 text-xs text-muted-foreground">{{ t('pages.loyaltyPage.tabs.recovery.onlineUrlHint') }}</p>
      </div>
    </div>

    <RecoveryExperienceModal :is-open="previewOpen" :method-key="previewKey" @close="previewOpen = false" />
  </div>
</template>
